/**
 * The part of an episode's page that is only that episode's: what happened in
 * it, in a sentence or three, and what its dish or its place is.
 *
 * Shared by the Worker, which writes it into the page, and by the app, which
 * draws the same when an episode is opened from inside it.
 *
 * The hand-written texts are in `src/racconti-testi.js`, passed in as
 * `racconti`: an episode with no entry there has only "La puntata".
 */


const FOUR_RESTAURANTS = '4-ristoranti';

/**
 * The places of the dish contests that are not towns, each with its
 * preposition: every other one is a town and takes "a". A new area needs
 * adding here, or it reads "a Langhe".
 */
const AREA_OF = {
  'Colli senesi': 'dei Colli senesi',
  Monferrato: 'del Monferrato',
  Poetto: 'al Poetto',
  Valtellina: 'della Valtellina',
};

/** "A Milano", "ad Ascoli Piceno", "della Valtellina"; "Monti, Roma" is "a Roma". */
export function inPlace(town) {
  if (AREA_OF[town]) return AREA_OF[town];
  const name = town.split(',').at(-1).trim();
  return `${/^a/i.test(name) ? 'ad' : 'a'} ${name}`;
}

const NUMBERS = ['', 'uno', 'due', 'tre', 'quattro', 'cinque', 'sei', 'sette', 'otto', 'nove', 'dieci'];
const inWords = (n) => NUMBERS[n] ?? String(n);

const esc = (value) =>
  String(value).replace(/[&<>"']/g, (character) => `&#${character.charCodeAt(0)};`);

/** "Il 12 ottobre 2026", "l'8 ottobre 2026", "nel marzo 2018". */
function onDate(date) {
  if (!/^\d/.test(date)) return `nel ${date}`;
  return /^(8|11|1) /.test(date) ? `l’${date}` : `il ${date}`;
}

/** The town in a confirmed address ("…, 61043 Cagli PU"), else the venue's zone. */
function townOf(venue) {
  const found =
    venue.indirizzo && venue.indirizzo_confermato
      ? /\b\d{5}\s+(.+?)\s+[A-Z]{2}\b/.exec(venue.indirizzo)
      : null;
  return (found ? found[1] : venue.zona ?? '').split(',').at(-1).trim();
}

function andList(names) {
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} e ${names.at(-1)}` : names[0] ?? '';
}

/**
 * "La puntata": which episode, who was in it and where, who won, how many are
 * open. Every fact is the catalogue's, so it is right for an episode added
 * tonight without anybody writing a line.
 *
 * `date` is the episode's date as the page shows it, `host` the presenter or
 * nothing, and `venues` the episode's own, winner first.
 */
export function episodeStory(episode, venues, { programName, host, date }) {
  const n = venues.length;
  if (!n) return null;
  const four = episode.programma === FOUR_RESTAURANTS;
  const noun = four ? 'ristoranti' : 'locali';
  const sentences = [];

  const aired = date ? `, andata in onda ${onDate(date)},` : '';
  const where = !four && episode.citta ? ` ${inPlace(episode.citta)}` : '';
  const named = episode.titolo ? ` «${episode.titolo}»` : '';
  sentences.push(
    host
      ? `Nella puntata${named} di ${programName}${aired} ${host} ha messo a confronto ${inWords(n)} ${noun}${where}.`
      : `Nella puntata${named} di ${programName}${aired} sono stati messi a confronto ${inWords(n)} ${noun}${where}.`
  );

  // A town beside each name only when they are not all in one: "La Gioconda
  // (Cagli)", but not "Sevilla mia (Milano)" on an episode set in Milan.
  const towns = venues.map(townOf);
  const oneTown = towns.every((town) => town && town === towns[0]);
  const names = venues.map((venue, i) => {
    const name = venue.nome ?? venue.id;
    return !oneTown && towns[i] ? `${name} (${towns[i]})` : name;
  });
  sentences.push(`In gara: ${andList(names)}.`);

  const winner = venues.find((venue) => venue.vincitore);
  if (winner) sentences.push(`Ha vinto ${winner.nome ?? winner.id}.`);

  // Said only once somebody has looked: on an episode added tonight, "nessuno
  // è aperto" would be about places nobody has checked yet.
  if (venues.some((venue) => venue.stato !== 'ignoto')) {
    const open = venues.filter((venue) => venue.stato === 'aperto').length;
    if (open === n) sentences.push(n === 2 ? 'Tutti e due risultano ancora aperti.' : `Tutti e ${inWords(n)} risultano ancora aperti.`);
    else if (open === 0) sentences.push(`Nessuno dei ${inWords(n)} risulta ancora aperto.`);
    else sentences.push(open === 1 ? `Uno su ${inWords(n)} risulta ancora aperto.` : `${open} su ${n} risultano ancora aperti.`);
  }
  return sentences.join(' ');
}

/** The whole section as HTML: "La puntata", then the dish or the place if written. */
export function episodeStoryHtml(episode, venues, options, racconti) {
  const story = episodeStory(episode, venues, options);
  const extra = racconti?.[episode.slug];
  const parts = [];
  if (story) parts.push(`<h2 class="story__label">La puntata</h2><p>${esc(story)}</p>`);
  if (extra?.piatto) parts.push(`<h2 class="story__label">Il piatto</h2><p>${esc(extra.piatto)}</p>`);
  if (extra?.territorio) parts.push(`<h2 class="story__label">Il territorio</h2><p>${esc(extra.territorio)}</p>`);
  return parts.join('');
}
